import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";
import { Sun, Moon, Search, Bell, CalendarDays, Activity, Menu, X, Sparkles, Plus, RefreshCw } from "lucide-react";
import API from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import auditLogo from "../assets/zentis-logo.png";

export default function Header({ onToggleMobileMenu }) {
  const navigate = useNavigate();
  const { theme, toggleTheme, auditRun, auditRuns, loadRun, setNewAuditDialogOpen, fetchRuns } = useAudit();
  const { logout, user } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const todayLabel = useMemo(() => {
    return new Date().toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  }, []);

  const userInitials = useMemo(() => {
    const raw = String(user?.name || user?.email || "A").trim();
    const parts = raw.split(/\s+/).filter(Boolean);
    const initials = parts.slice(0, 2).map((part) => part[0]).join("");
    return (initials || raw[0] || "A").toUpperCase();
  }, [user?.email, user?.name]);

  const filteredRuns = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return auditRuns.slice(0, 8);
    return auditRuns
      .filter((r) => String(r.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [auditRuns, searchText]);

  const switchAudit = useCallback(async (run) => {
    if (!run?.id) return;
    try {
      const loaded = await loadRun(run.id);
      setSearchOpen(false);
      setSearchText(run.name || "");
      navigate(`/phase/${loaded?.current_phase || 1}`);
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error("Your session expired. Please sign in again.");
        logout();
        navigate("/login", { replace: true });
        return;
      }
      if (error?.response?.status === 403) {
        toast.error("You do not have access to this audit.");
        return;
      }
      toast.error(error?.response?.data?.detail || "Failed to open audit");
    }
  }, [loadRun, logout, navigate]);

  const fetchNotifications = useCallback(async () => {
    try {
      const params = { limit: 8 };
      if (auditRun?.id) params.run_id = auditRun.id;
      const { data } = await API.get("/audit-logs", { params });
      setNotifications(Array.isArray(data) ? data : []);
    } catch {
      setNotifications([]);
    }
  }, [auditRun?.id]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const formatNotificationTime = (value) => {
    if (!value) return "Just now";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Just now";
    return date.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="header-shell flex h-full items-center justify-between px-4 lg:px-6" data-testid="header">
      {/* Left: Mobile Toggle, Brand Logo & Active Audit Chip */}
      <div className="flex items-center gap-3 min-w-0">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-accent rounded-xl shrink-0"
          onClick={onToggleMobileMenu}
          aria-label="Toggle navigation menu"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Mobile Zentis Brand Logo */}
        <div className="flex lg:hidden items-center shrink-0">
          <img src={auditLogo} alt="Zentis Logo" className="h-6 w-auto object-contain" />
        </div>

        <div className="flex items-center gap-2 min-w-0">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300 max-w-[200px] sm:max-w-[320px] truncate shadow-sm">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
            <span className="truncate">
              {auditRun?.name ? `Active: ${auditRun.name}` : "No active audit"}
            </span>
          </div>
        </div>
      </div>

      {/* Center: Search input */}
      <div className="hidden sm:flex items-center justify-center flex-1 max-w-md mx-4">
        <div className="relative w-full">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search audit runs..."
            className="w-full h-9 pl-9 pr-8 text-xs bg-muted/50 border-border/80 rounded-full focus-visible:ring-emerald-500/30 focus-visible:border-emerald-500/50 transition-all"
            value={searchText}
            onFocus={() => setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && filteredRuns.length > 0) switchAudit(filteredRuns[0]);
            }}
          />
          {searchText && (
            <button
              onClick={() => setSearchText("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {searchOpen && (
            <div className="absolute top-full left-0 right-0 z-50 mt-2 rounded-2xl border border-border bg-card p-1.5 shadow-2xl backdrop-blur-xl">
              {filteredRuns.length > 0 ? (
                filteredRuns.map((run) => (
                  <button
                    key={run.id}
                    className="w-full flex items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-medium text-foreground hover:bg-emerald-500/10 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      switchAudit(run);
                    }}
                  >
                    <span className="truncate">{run.name}</span>
                    <span className="text-[10px] text-muted-foreground font-mono">Phase {run.current_phase || 1}</span>
                  </button>
                ))
              ) : (
                <p className="px-3 py-2 text-xs text-muted-foreground text-center">No audit runs found</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Actions: Notifications, Date, Theme, User */}
      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        {/* Notifications Popover */}
        <Popover onOpenChange={(open) => open && fetchNotifications()}>
          <PopoverTrigger asChild>
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent"
                aria-label="Open notifications"
              >
                <Bell className="w-4 h-4" />
              </Button>
              {notifications.length > 0 && (
                <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-[9px] font-bold text-white shadow-sm animate-pulse">
                  {Math.min(notifications.length, 9)}
                </span>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[340px] sm:w-[380px] p-0 rounded-2xl border-border bg-card shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-4 py-3.5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-100">Audit Logs & Activity</p>
                  <p className="text-sm font-semibold">Notifications</p>
                </div>
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
                  <Activity className="h-4 w-4" />
                </div>
              </div>
            </div>
            <ScrollArea className="max-h-72">
              <div className="p-2 space-y-1">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    <Sparkles className="mx-auto h-5 w-5 mb-2 text-emerald-500/50" />
                    All caught up! No new notifications.
                  </div>
                ) : (
                  notifications.map((n) => (
                    <button
                      key={n.id}
                      className="w-full flex items-start gap-3 rounded-xl p-2.5 text-left transition-colors hover:bg-accent/60 group"
                      onClick={() => navigate("/logs")}
                    >
                      <div className="h-7 w-7 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 mt-0.5">
                        <Activity className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-foreground truncate group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                          {String(n.action || "audit_update").replace(/_/g, " ")}
                        </p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {n.audit_run_name || (n.audit_run_id ? `Audit #${n.audit_run_id}` : "System event")}
                        </p>
                        <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono">
                          {formatNotificationTime(n.timestamp || n.created_at)}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
            {notifications.length > 0 && (
              <div className="p-2 border-t border-border bg-muted/40 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/logs")}
                  className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                  View full audit log stream →
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {/* Date Indicator Pill */}
        <div className="hidden md:inline-flex items-center gap-1.5 rounded-xl border border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground font-medium">
          <CalendarDays className="w-3.5 h-3.5 text-emerald-500" />
          <span>{todayLabel}</span>
        </div>

        {/* Theme Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          data-testid="theme-toggle-btn"
          aria-label="Toggle theme"
          className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent"
        >
          {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-700" />}
        </Button>

        {/* User Profile Avatar */}
        <button
          type="button"
          onClick={() => navigate("/profile")}
          className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-600 to-teal-700 text-xs font-bold text-white shadow-md hover:scale-105 transition-transform"
          aria-label="Open profile"
          title={user?.name || "Profile"}
        >
          {userInitials}
        </button>
      </div>
    </div>
  );
}

